/**
 * Daily quota store — tracks usage limits that reset each day.
 * Persisted to localStorage. Resets automatically when the date changes.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const DAILY_LIMITS = {
  cvGenerations: 5,
  coverLetters: 3,
  vacancySaves: 20,
} as const;

interface DailyQuotaState {
  date: string;           // YYYY-MM-DD format
  cvGenerations: number;  // count used today
  coverLetters: number;
  vacancySaves: number;
}

interface DailyQuotaActions {
  checkAndResetDate: () => void;
  canGenerate: () => boolean;
  canSaveCoverLetter: () => boolean;
  canSaveVacancy: () => boolean;
  consumeGeneration: () => boolean;
  consumeCoverLetter: () => boolean;
  consumeVacancy: () => boolean;
  getRemainingGenerations: () => number;
  getRemainingCoverLetters: () => number;
  getRemainingVacancies: () => number;
}

export type DailyQuotaStore = DailyQuotaState & DailyQuotaActions;

/**
 * Get today's date in YYYY-MM-DD format (local timezone).
 */
function getTodayString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const useDailyQuotaStore = create<DailyQuotaStore>()(
  persist(
    (set, get) => ({
      // State
      date: getTodayString(),
      cvGenerations: 0,
      coverLetters: 0,
      vacancySaves: 0,

      // Actions

      /**
       * Check if the stored date matches today. If not, reset all counters.
       */
      checkAndResetDate: () => {
        const today = getTodayString();
        if (get().date !== today) {
          set({
            date: today,
            cvGenerations: 0,
            coverLetters: 0,
            vacancySaves: 0,
          });
        }
      },

      /**
       * Check if user can generate another CV today.
       */
      canGenerate: () => {
        get().checkAndResetDate();
        return get().cvGenerations < DAILY_LIMITS.cvGenerations;
      },

      /**
       * Check if user can save another cover letter today.
       */
      canSaveCoverLetter: () => {
        get().checkAndResetDate();
        return get().coverLetters < DAILY_LIMITS.coverLetters;
      },

      /**
       * Check if user can save another vacancy today.
       */
      canSaveVacancy: () => {
        get().checkAndResetDate();
        return get().vacancySaves < DAILY_LIMITS.vacancySaves;
      },

      /**
       * Consume one CV generation quota.
       * Returns true if successful, false if limit reached.
       */
      consumeGeneration: () => {
        get().checkAndResetDate();
        const current = get().cvGenerations;
        if (current >= DAILY_LIMITS.cvGenerations) {
          return false;
        }
        set({ cvGenerations: current + 1 });
        return true;
      },

      /**
       * Consume one cover letter quota.
       * Returns true if successful, false if limit reached.
       */
      consumeCoverLetter: () => {
        get().checkAndResetDate();
        const current = get().coverLetters;
        if (current >= DAILY_LIMITS.coverLetters) {
          return false;
        }
        set({ coverLetters: current + 1 });
        return true;
      },

      /**
       * Consume one vacancy save quota.
       * Returns true if successful, false if limit reached.
       */
      consumeVacancy: () => {
        get().checkAndResetDate();
        const current = get().vacancySaves;
        if (current >= DAILY_LIMITS.vacancySaves) {
          return false;
        }
        set({ vacancySaves: current + 1 });
        return true;
      },

      /**
       * Get remaining CV generation slots for today.
       */
      getRemainingGenerations: () => {
        get().checkAndResetDate();
        return Math.max(0, DAILY_LIMITS.cvGenerations - get().cvGenerations);
      },

      /**
       * Get remaining cover letter slots for today.
       */
      getRemainingCoverLetters: () => {
        get().checkAndResetDate();
        return Math.max(0, DAILY_LIMITS.coverLetters - get().coverLetters);
      },

      /**
       * Get remaining vacancy save slots for today.
       */
      getRemainingVacancies: () => {
        get().checkAndResetDate();
        return Math.max(0, DAILY_LIMITS.vacancySaves - get().vacancySaves);
      },
    }),
    {
      name: 'daily-quota-storage',
      partialize: (state) => ({
        date: state.date,
        cvGenerations: state.cvGenerations,
        coverLetters: state.coverLetters,
        vacancySaves: state.vacancySaves,
      }),
    }
  )
);

export { DAILY_LIMITS };
